import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

import { LinkToParam } from "../components/ui/Link";
import { path, serverResponsePaths } from "../paths";
import { useAuthentication } from "../providers/AuthenticationProvider";

type Props = {
  replace?: boolean;
  to: LinkToParam;
};

export default function useRedirect() {
  const navigate = useNavigate();
  const { user } = useAuthentication();

  const redirect = useCallback(
    (to: LinkToParam, options?: { replace?: boolean }) => {
      const destination = typeof to === "string" ? to : path(...to);
      const needsServerResponse =
        typeof to === "string" || Object.keys(serverResponsePaths).includes(to[0] as keyof typeof serverResponsePaths);

      const openUrl = (url: string) => {
        if (options?.replace) location.replace(url);
        else location.href = url;
      };

      console.log("Redirecting to", to);
      // Target will be a path if it is on the same domain, otherwise it will be a URL
      if (destination.startsWith("/") && !needsServerResponse) {
        void navigate(destination, { replace: options?.replace });
        return;
      }
      if (
        destination.startsWith(location.origin) ||
        typeof to === "string" ||
        !destination.startsWith("http") ||
        to[0].startsWith("up.") ||
        !user
      ) {
        openUrl(destination);
        return;
      }

      openUrl(path("up.auth.loginRedirect", { redirectUrl: destination }));
    },
    [navigate, user],
  );

  return redirect;
}

export function Redirect({ replace = true, to }: Props): React.ReactElement {
  const redirect = useRedirect();

  useEffect(() => {
    redirect(to, { replace });
  }, [to, redirect, replace]);

  return <></>;
}
